import { useState } from "react";
import { Check, Copy, SquareTerminal } from "lucide-react";
import { toast } from "sonner";
import { BACKEND } from "@/lib/api";


const copyText = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        try {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand("copy");
            ta.remove();
            return ok;
        } catch {
            return false;
        }
    }
};

const Command = ({ title, cmd, testid }) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        if (await copyText(cmd)) {
            setCopied(true);
            toast.success("Copied");
            setTimeout(() => setCopied(false), 1500);
        } else {
            toast.error("Copy failed — select and copy manually");
        }
    };
    return (
        <div className="overflow-hidden rounded-lg border border-slate-800/80 bg-slate-950/60">
            <div className="flex items-center justify-between border-b border-slate-800/60 px-4 py-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {title}
                </span>
                <button
                    data-testid={testid}
                    onClick={copy}
                    className="flex items-center gap-1.5 rounded border border-slate-700 px-2.5 py-1 font-mono text-[10px] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                >
                    {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                    {copied ? "copied" : "copy"}
                </button>
            </div>
            <pre className="overflow-x-auto p-4 font-mono text-[11px] leading-relaxed text-cyan-100">
                {cmd}
            </pre>
        </div>
    );
};

export default function CurlPanel({ appId, app }) {
    const template = app?.template;
    // a key created this session is pre-filled; older keys are never shown again
    const key = sessionStorage.getItem(`mdh-new-key-${appId}`) || "YOUR_PUBLISH_KEY";

    const cmds = [
        {
            title: "Publish an event",
            id: "ingest",
            cmd: `curl -X POST ${BACKEND}/api/ingest/${appId}/events \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${key}" \\
  -d '{"type":"order.created","data":{"order_id":"ord_123","total":129.00}}'`,
        },
        {
            title: "Grab the OpenAPI spec",
            id: "spec",
            cmd: `curl -H "X-API-Key: ${key}" ${BACKEND}/api/apps/${appId}/spec`,
        },
        {
            title: "Listen live from your terminal",
            id: "listen",
            cmd: `curl -o mdh_listen.mjs ${BACKEND}/api/agent/listen-script
MDH_TOKEN=mdh_pat_… node mdh_listen.mjs ${appId}

# token: Settings → API → Create token`,
        },
    ];

    if (template === "webhook-receiver" && app?.hook_slug) {
        cmds.unshift({
            title: "Send a webhook — public URL, no key needed",
            id: "hook",
            cmd: `curl -X POST ${BACKEND}/api/hooks/${app.hook_slug} \\
  -H "Content-Type: application/json" \\
  -d '{"type":"invoice.paid","amount":12900,"currency":"usd"}'`,
        });
    }
    if (template === "local-machine-monitor") {
        cmds.push({
            title: "Run the machine agent",
            id: "agent",
            cmd: `pip install psutil requests
curl -o mdh_agent.py ${BACKEND}/api/agent/script
python mdh_agent.py --api ${BACKEND} --key ${key}`,
        });
    }
    if (template === "node-project-monitor") {
        cmds.push({
            title: "Node middleware heartbeat (what mdh-monitor.mjs sends)",
            id: "node",
            cmd: `curl -X POST ${BACKEND}/api/agent/node-metrics \\
  -H "Content-Type: application/json" \\
  -H "X-Agent-Key: ${key}" \\
  -d '{"routes":[{"method":"GET","route":"/api/users","rpm":42,"err_pct":0.5,"p95":87}],"heap_mb":96.4}'`,
        });
    }
    if (template === "webhook-receiver") {
        cmds.push({
            title: "Dev tunnel — receive webhooks on localhost",
            id: "tunnel",
            cmd: `curl -o mdh_tunnel.mjs ${BACKEND}/api/agent/tunnel-script
MDH_TOKEN=mdh_pat_… node mdh_tunnel.mjs ${appId} --to http://localhost:8080

# while it runs, your local handler's response
# goes back to the producer (Stripe, GitHub…)`,
        });
    }
    if (template === "outbound-webhooks") {
        cmds.push({
            title: "Publish to your endpoints",
            id: "publish",
            cmd: `# token: Settings → API → Create token
curl -X POST ${BACKEND}/api/apps/${appId}/publish \\
  -H "Authorization: Bearer mdh_pat_…" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"order.created","data":{"order_id":"ord_123"}}'`,
        });
    }
    if (template === "rest-api-monitor") {
        cmds.push({
            title: "Read uptime history",
            id: "uptime",
            cmd: `# token: Settings → API → Create token
curl -H "Authorization: Bearer mdh_pat_…" \\
  ${BACKEND}/api/apps/${appId}/uptime`,
        });
    }
    if (template === "cron-monitor") {
        cmds.unshift({
            title: "Job heartbeat — add as the last line of any cron job",
            id: "heartbeat",
            cmd: `curl -X POST ${BACKEND}/api/agent/heartbeat \\
  -H "Content-Type: application/json" \\
  -H "X-Agent-Key: ${key}" \\
  -d '{"job":"nightly-backup","host":"$(hostname)"}'

# crontab example — beats every night at 03:00 after the backup:
# 0 3 * * * /usr/local/bin/backup.sh && curl -s -X POST ${BACKEND}/api/agent/heartbeat -H "Content-Type: application/json" -H "X-Agent-Key: ${key}" -d '{"job":"nightly-backup"}'`,
        });
    }
    if (template === "ssl-cert-monitor") {
        cmds.unshift({
            title: "Nothing to run — set the domain above and MDH checks it hourly",
            id: "ssl-note",
            cmd: `# server-side monitor — no agent needed
# dashboard: set the domain in the SSL certificate panel
# or via the API (token: Settings → API → Create token):
curl -X PATCH ${BACKEND}/api/apps/${appId}/config \\
  -H "Authorization: Bearer mdh_pat_…" \\
  -H "Content-Type: application/json" \\
  -d '{"config":{"domain":"example.com"}}'`,
        });
    }
    if (template === "docker-monitor") {
        cmds.push({
            title: "Run the Docker agent",
            id: "docker",
            cmd: `curl -o mdh_docker_agent.mjs ${BACKEND}/api/agent/docker-script
node mdh_docker_agent.mjs --api ${BACKEND} --key ${key}`,
        });
    }
    if (template === "log-watcher") {
        cmds.push({
            title: "Run the log watcher",
            id: "log",
            cmd: `curl -o mdh_log_agent.mjs ${BACKEND}/api/agent/log-script
node mdh_log_agent.mjs --api ${BACKEND} --key ${key} \\
  --file /var/log/syslog --match "error|fail"`,
        });
    }

    return (
        <div data-testid="curl-panel" className="glass-panel rounded-xl p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                <SquareTerminal className="h-4 w-4 text-cyan-400" />
                Integrate with curl
            </h3>
            <p className="mt-1.5 font-mono text-[10px] text-slate-500">
                Pre-filled for this app.{" "}
                {key.startsWith("mdk_")
                    ? "Your freshly created key is already in place."
                    : "Create a publish key in the API keys panel below, then paste it in."}
            </p>
            <div className="mt-4 space-y-3">
                {cmds.map((c) => (
                    <Command key={c.id} title={c.title} cmd={c.cmd} testid={`curl-copy-${c.id}`} />
                ))}
            </div>
        </div>
    );
}
